import React, { useCallback, useEffect, useState } from 'react';
import {
  TALKALYST_URL,
  dismissTalkalystNudge,
  getSchoolClock,
  isTalkalystNudgeDismissed,
  isTalkalystNudgeWindow,
} from '../utils/talkalystNudge';
import './TalkalystNudge.css';

const TRACKS = [
  { label: 'Read', color: '#2A6F6A' },
  { label: 'Watch', color: '#5C4B8A' },
  { label: 'Speak', color: '#C45C26' },
  { label: 'Drill', color: '#2D5FF5' },
] as const;

type TalkalystNudgeProps = {
  /** Injected clock for tests. Live usage ticks every minute. */
  now?: Date;
};

function useSchoolNow(override?: Date): Date {
  const [now, setNow] = useState(() => override ?? new Date());

  useEffect(() => {
    if (override) {
      setNow(override);
      return;
    }
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, [override]);

  return now;
}

const TalkalystNudge: React.FC<TalkalystNudgeProps> = ({ now: nowOverride }) => {
  const now = useSchoolNow(nowOverride);
  const clock = getSchoolClock(now);
  const inWindow = isTalkalystNudgeWindow(now);
  const [dismissed, setDismissed] = useState(() => isTalkalystNudgeDismissed(clock.dateKey));

  useEffect(() => {
    setDismissed(isTalkalystNudgeDismissed(clock.dateKey));
  }, [clock.dateKey]);

  const handleDismiss = useCallback(() => {
    dismissTalkalystNudge(clock.dateKey);
    setDismissed(true);
  }, [clock.dateKey]);

  if (!inWindow || dismissed) return null;

  const afterLessons = clock.isWeekday;

  return (
    <aside
      className="talkalyst-nudge talkalyst-nudge--evening"
      aria-label="Talkalyst practice reminder"
      aria-live="polite"
    >
      <div className="talkalyst-nudge__mark" aria-hidden="true">
        <span className="talkalyst-nudge__bar" />
        <span className="talkalyst-nudge__bar" />
        <span className="talkalyst-nudge__bar" />
        <span className="talkalyst-nudge__bar" />
      </div>

      <div className="talkalyst-nudge__copy">
        <p className="talkalyst-nudge__kicker">{afterLessons ? 'After lessons' : 'Talkalyst'}</p>
        <p className="talkalyst-nudge__title">
          {afterLessons ? 'Lessons are done.' : 'Log today’s English.'}
        </p>
        <p className="talkalyst-nudge__body">
          {afterLessons ? 'Log today’s English before the day closes.' : 'Before the day closes.'}
        </p>
        <ul className="talkalyst-nudge__tracks">
          {TRACKS.map((track) => (
            <li key={track.label} style={{ '--track': track.color } as React.CSSProperties}>
              {track.label}
            </li>
          ))}
        </ul>
      </div>

      <div className="talkalyst-nudge__actions">
        <button
          type="button"
          className="talkalyst-nudge__dismiss"
          onClick={handleDismiss}
        >
          Not now
        </button>
        <a
          className="talkalyst-nudge__cta"
          href={TALKALYST_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleDismiss}
        >
          Open today
        </a>
      </div>
    </aside>
  );
};

export default TalkalystNudge;
